import type {ResultPresentationBinding, ResultPresentationReceipt} from '@github-ui/ahp-relay/relay-result-presentation'
import type {BrowserObservationTime} from './browser-observation-clock'
import {
  createPresentationLossReporter,
  type ResultPresentationLoss,
  type ResultPresentationCoverageReason,
} from '@github-ui/ahp-relay/result-presentation-health'
import {
  RESULT_PRESENTATION_POLICY,
  type ResultPresentationControls,
  type ResultPresentationFact,
  type ResultPresentationEvent,
  type ResultPresentationMilestone,
  type ResultPresentationObservation,
  type ResultPresentationUnknown,
} from './result-presentation-contract'

interface Dependencies {
  now(): BrowserObservationTime
  nextSequence(): number
  visibility(): ResultPresentationFact['visibility']
  role(): ResultPresentationFact['role']
  emit(fact: ResultPresentationEvent): void | Promise<void>
  reportError(reason: 'observer' | 'clock' | 'emission' | 'observation_capacity'): void | Promise<void>
}

export const MAX_PRESENTATION_MILESTONES = 256

export function samePresentationBinding(expected: ResultPresentationBinding, actual: ResultPresentationBinding) {
  return (
    actual.task_id === expected.task_id &&
    actual.session_id === expected.session_id &&
    actual.environment_id === expected.environment_id &&
    actual.chat_id === expected.chat_id
  )
}

export function validPresentationControls(
  controls: Omit<ResultPresentationControls, 'role'> & {role: ResultPresentationFact['role']},
): boolean {
  if (controls.role === 'passive' || controls.role === 'read_only') {
    return (
      controls.control_scenario === controls.role &&
      controls.action_mode === 'blocked' &&
      !controls.input_editable &&
      !controls.send_enabled_for_valid_input &&
      !controls.stop_present
    )
  }
  if (controls.role !== 'writable') return false
  if (!controls.input_editable || !controls.send_enabled_for_valid_input) return false
  if (controls.control_scenario === 'newer_active') {
    return (
      Boolean(controls.active_turn_id && controls.active_turn_id !== controls.turn_id) &&
      controls.stop_present &&
      (controls.action_mode === 'steer' || controls.action_mode === 'queue')
    )
  }
  return (
    controls.control_scenario === 'settled_writable' &&
    !controls.active_turn_id &&
    !controls.stop_present &&
    controls.action_mode === 'send'
  )
}

export function createBrowserResultPresentationTracker(
  dependencies: Dependencies,
  context: {
    client_view_id: string
    surface: ResultPresentationFact['surface']
    binding: Omit<ResultPresentationBinding, 'chat_id'>
  },
) {
  const milestones = new Map<string, ResultPresentationMilestone>()
  let disposed = false
  let exhausted = false
  const emittedSourceLosses = new Set<string>()
  const sourceLossOrder: string[] = []
  const reported = new Set<Parameters<Dependencies['reportError']>[0]>()
  const report = async (reason: Parameters<Dependencies['reportError']>[0]) => {
    if (reported.has(reason)) return
    reported.add(reason)
    try {
      await dependencies.reportError(reason)
    } catch {
      // eslint-disable-next-line no-console
      console.error('Unable to report result presentation observation error')
    }
  }
  const observe = (callback: () => void) => {
    try {
      callback()
    } catch {
      report('observer')
    }
  }
  const observeDelivery = async (delivery: Promise<void>) => {
    try {
      await delivery
    } catch {
      report('emission')
    }
  }
  const key = (chat: string, turn: string, kind: 'content' | 'terminal') => JSON.stringify([chat, turn, kind])
  const emitCoverage = (
    scope: 'source' | 'view',
    reason: ResultPresentationCoverageReason,
    loss: ResultPresentationLoss,
  ) => {
    const now = dependencies.now()
    const sequence = dependencies.nextSequence()
    if (
      !now.clockId ||
      !Number.isFinite(now.monotonicMs) ||
      !Number.isSafeInteger(sequence) ||
      sequence < 0 ||
      !Number.isFinite(loss.monotonicMs) ||
      loss.monotonicMs < 0 ||
      loss.monotonicMs > now.monotonicMs ||
      !Number.isFinite(loss.observedAtMs) ||
      loss.observedAtMs < 0 ||
      !Number.isSafeInteger(loss.suppressed_observations) ||
      loss.suppressed_observations < 0
    ) {
      report('clock')
      return
    }
    if (scope === 'source') {
      const lossKey = JSON.stringify([reason, loss.source_event_id])
      if (emittedSourceLosses.has(lossKey)) return
      emittedSourceLosses.add(lossKey)
      sourceLossOrder.push(lossKey)
      if (sourceLossOrder.length > MAX_PRESENTATION_MILESTONES) {
        const oldest = sourceLossOrder.shift()
        if (oldest) emittedSourceLosses.delete(oldest)
      }
    }
    try {
      const delivery = dependencies.emit({
        ...RESULT_PRESENTATION_POLICY,
        task_id: context.binding.task_id,
        session_id: context.binding.session_id,
        environment_id: context.binding.environment_id,
        observation: 'coverage_lost',
        evidence_scope: scope,
        reason,
        ...(scope === 'view' ? {client_view_id: context.client_view_id} : {}),
        event_id: `${now.clockId}/${sequence}`,
        source_event_id: loss.source_event_id,
        clock_id: now.clockId,
        sequence,
        observed_at_ms: loss.observedAtMs,
        monotonic_ms: loss.monotonicMs,
        surface: scope === 'source' ? 'unknown' : context.surface,
        role: scope === 'source' ? 'unknown' : dependencies.role(),
        visibility: loss.visibility,
        suppressed_observations: loss.suppressed_observations,
      })
      if (delivery) void observeDelivery(delivery)
    } catch {
      report('emission')
    }
  }
  let healthSequence = 0
  const reporters = new Map<ResultPresentationCoverageReason, ReturnType<typeof createPresentationLossReporter>>()
  const coverageLost = (
    reason: ResultPresentationCoverageReason,
    loss?: ResultPresentationLoss,
    opportunity: string = reason,
  ) => {
    if (disposed) return
    observe(() => {
      if (reason !== 'observation_capacity' && reason !== 'entry_unverified') {
        report('observer')
        return
      }
      if (reason === 'observation_capacity') {
        exhausted = true
        for (const milestone of milestones.values()) milestone.unresolved(reason)
        report('observation_capacity')
      }
      if (loss) {
        emitCoverage('source', reason, loss)
        return
      }
      let reporter = reporters.get(reason)
      if (!reporter) {
        reporter = createPresentationLossReporter({
          now: () => ({...dependencies.now(), visibility: dependencies.visibility()}),
          id: () => `${context.client_view_id}/coverage/${healthSequence++}`,
          emit: value => emitCoverage('view', reason, value),
        })
        reporters.set(reason, reporter)
      }
      reporter(opportunity)
    })
  }

  const begin = (input: ResultPresentationReceipt): ResultPresentationMilestone | undefined => {
    const unitKey = key(input.chat_id, input.turn_id, input.milestone_kind)
    const existing = milestones.get(unitKey)
    if (existing || disposed) return existing
    if (exhausted || milestones.size >= MAX_PRESENTATION_MILESTONES) {
      coverageLost('observation_capacity', undefined, unitKey)
      return
    }
    let milestone: ResultPresentationMilestone | undefined
    observe(() => {
      const clock = dependencies.now()
      if (
        !clock.clockId ||
        !Number.isFinite(input.monotonicMs) ||
        input.monotonicMs < 0 ||
        input.monotonicMs > clock.monotonicMs ||
        !Number.isFinite(input.observedAtMs) ||
        input.observedAtMs < 0
      ) {
        report('clock')
        return
      }
      // Project source fields explicitly. No content, arbitrary metadata or DOM
      // values may pass through an otherwise structurally compatible object.
      const receipt: ResultPresentationReceipt = {
        task_id: input.task_id,
        session_id: input.session_id,
        environment_id: input.environment_id,
        chat_id: input.chat_id,
        turn_id: input.turn_id,
        source_event_id: input.source_event_id,
        ...(input.upstream_source_event_id ? {upstream_source_event_id: input.upstream_source_event_id} : {}),
        milestone_kind: input.milestone_kind,
        receipt_source: input.receipt_source,
        terminal_provenance: input.terminal_provenance,
        observedAtMs: input.observedAtMs,
        monotonicMs: input.monotonicMs,
        visibility: input.visibility,
        ...(input.part_id ? {part_id: input.part_id} : {}),
        ...(input.terminal_state ? {terminal_state: input.terminal_state} : {}),
      }
      let receiptEventId: string | undefined
      let presentation: {id: string; time: number; confirmed: boolean; role: ResultPresentationFact['role']} | undefined
      let controls: {id: string; time: number} | undefined
      let closedAt: number | undefined
      let failureTime: number | undefined
      let visibilityInterrupted = receipt.visibility !== 'visible'
      const unknowns = new Set<ResultPresentationUnknown>()
      const emit = (observation: ResultPresentationObservation, time = dependencies.now()): string | undefined => {
        const sequence = dependencies.nextSequence()
        if (
          time.clockId !== clock.clockId ||
          !Number.isSafeInteger(sequence) ||
          sequence < 0 ||
          !Number.isFinite(time.observedAtMs) ||
          !Number.isFinite(time.monotonicMs) ||
          time.monotonicMs < receipt.monotonicMs
        ) {
          report('clock')
          return
        }
        const eventId = `${time.clockId}/${sequence}`
        receiptEventId ??= eventId
        const {observedAtMs: _observedAtMs, monotonicMs, visibility: receiptVisibility, ...identity} = receipt
        const visibility = dependencies.visibility()
        if (visibility === 'hidden') visibilityInterrupted = true
        try {
          const delivery = dependencies.emit({
            ...RESULT_PRESENTATION_POLICY,
            ...identity,
            client_view_id: context.client_view_id,
            surface: context.surface,
            event_id: eventId,
            receipt_event_id: receiptEventId,
            clock_id: time.clockId,
            sequence,
            observed_at_ms: time.observedAtMs,
            monotonic_ms: time.monotonicMs,
            receipt_monotonic_ms: monotonicMs,
            receipt_visibility: receiptVisibility,
            visibility,
            ...(visibilityInterrupted ? {visibility_interrupted: true as const} : {}),
            role: dependencies.role(),
            required_obligations: receipt.milestone_kind === 'content' ? 'content_presentation' : 'terminal_controls',
            ...observation,
          })
          if (delivery) void observeDelivery(delivery)
        } catch {
          report('emission')
        }
        return eventId
      }
      const finish = () => {
        if (exhausted || closedAt !== undefined || !presentation) return
        if (receipt.milestone_kind === 'content' && !presentation.confirmed) return
        if (receipt.milestone_kind === 'terminal' && !controls) return
        const closure = Math.max(presentation.time, controls?.time ?? presentation.time)
        if (failureTime !== undefined && failureTime <= closure) return
        closedAt = closure
        emit({
          observation: 'completed',
          presentation_event_id: presentation.id,
          ...(controls ? {controls_event_id: controls.id} : {}),
          closure_time_ms: closedAt,
        })
      }
      const unknown = (reason: ResultPresentationUnknown) => {
        if (closedAt !== undefined || unknowns.has(reason)) return
        if (reason === 'hidden') visibilityInterrupted = true
        unknowns.add(reason)
        emit({observation: 'unresolved', reason})
      }
      milestone = {
        receipt,
        get closed() {
          return closedAt !== undefined
        },
        presented: proof =>
          observe(() => {
            if (disposed || exhausted || closedAt !== undefined) return
            if (
              !samePresentationBinding(receipt, proof) ||
              proof.turn_id !== receipt.turn_id ||
              (receipt.milestone_kind === 'content' && proof.part_id !== receipt.part_id) ||
              (receipt.milestone_kind === 'terminal' && proof.terminal_state !== receipt.terminal_state)
            ) {
              milestone?.failed('binding_mismatch')
              return
            }
            const now = dependencies.now()
            if (
              proof.clock_id !== clock.clockId ||
              !Number.isFinite(proof.witness_time_ms) ||
              proof.witness_time_ms <= 0 ||
              proof.witness_time_ms < receipt.monotonicMs ||
              proof.witness_time_ms > now.monotonicMs
            ) {
              unknown('stale_paint')
              return
            }
            if (dependencies.visibility() !== 'visible') {
              unknown('hidden')
              return
            }
            const confirmed = proof.witness_method === 'element_timing'
            const role = dependencies.role()
            if (
              presentation &&
              (receipt.milestone_kind === 'content' ? presentation.confirmed || !confirmed : presentation.role === role)
            )
              return
            const id = emit({
              observation: 'presentation_observed',
              witness_method: proof.witness_method,
              presentation_coverage: confirmed ? 'confirmed_paint' : 'semantic_visibility_not_paint',
              witness_time_ms: proof.witness_time_ms,
              presentation_elapsed_ms: proof.witness_time_ms - receipt.monotonicMs,
            })
            if (id) {
              presentation = {id, time: proof.witness_time_ms, confirmed, role}
              controls = undefined
            }
            if (receipt.milestone_kind === 'content' && !confirmed) unknown('paint_unobserved')
            finish()
          }),
        controls: actual =>
          observe(() => {
            if (disposed || exhausted || closedAt !== undefined || receipt.milestone_kind !== 'terminal') return
            if (!samePresentationBinding(receipt, actual) || actual.turn_id !== receipt.turn_id) {
              milestone?.failed('binding_mismatch')
              return
            }
            if (dependencies.visibility() !== 'visible') {
              unknown('hidden')
              return
            }
            controls = undefined
            if (!presentation) {
              unknown('terminal_unobserved')
              return
            }
            if (
              !validPresentationControls(actual) ||
              actual.role !== dependencies.role() ||
              actual.role !== presentation.role
            ) {
              unknown('stale_controls')
              return
            }
            const now = dependencies.now()
            const id = emit({
              observation: 'controls_observed',
              witness_method: 'dom_visible_frame',
              presentation_coverage: 'semantic_visibility_not_paint',
              witness_time_ms: now.monotonicMs,
              controls_elapsed_ms: now.monotonicMs - receipt.monotonicMs,
              role: actual.role,
              control_scenario: actual.control_scenario,
              action_mode: actual.action_mode,
              input_editable: actual.input_editable,
              send_enabled_for_valid_input: actual.send_enabled_for_valid_input,
              stop_present: actual.stop_present,
              ...(actual.active_turn_id ? {active_turn_id: actual.active_turn_id} : {}),
            })
            if (id) controls = {id, time: now.monotonicMs}
            finish()
          }),
        unresolved: reason => observe(() => unknown(reason)),
        failed: (reason, time = dependencies.now()) =>
          observe(() => {
            if (disposed || (closedAt !== undefined && time.monotonicMs > closedAt)) return
            if (failureTime !== undefined && failureTime <= time.monotonicMs) return
            failureTime = time.monotonicMs
            emit({observation: 'failed', reason}, time)
          }),
      }
      milestones.set(unitKey, milestone)
      emit(
        {observation: 'receipt'},
        {clockId: clock.clockId, observedAtMs: receipt.observedAtMs, monotonicMs: receipt.monotonicMs},
      )
      if (receipt.visibility !== 'visible') unknown('hidden')
    })
    return milestone
  }
  return {
    begin,
    coverageLost,
    get exhausted() {
      return exhausted
    },
    get: (chat: string, turn: string, kind: 'content' | 'terminal') => milestones.get(key(chat, turn, kind)),
    all: () => milestones.values(),
    dispose(reason: 'disposed' | 'page_exit' | 'replaced' = 'disposed') {
      if (disposed) return
      for (const milestone of milestones.values()) milestone.unresolved(reason)
      disposed = true
      milestones.clear()
    },
  }
}
